/**
 * Workflow Form Schema
 *
 * Zod validation for workflow creation/edit form.
 *
 * SCALABILITY NOTE:
 * - Trigger configs use a flexible schema that validates base fields only
 * - Backend is the source of truth for trigger-specific validation
 * - New triggers can be added without changing this schema
 */

import { z } from "zod";
import { isValidTimezone } from "@/lib/timezone";
import type { Workflow } from "@/types/features/workflowTypes";
import { describeCron } from "../utils/cronUtils";

// =============================================================================
// TRIGGER CONFIG SCHEMAS
// =============================================================================

// Built-in triggers (schedule, manual) have strict schemas since they're frontend-controlled
const scheduleTriggerConfigSchema = z.object({
  type: z.literal("schedule"),
  enabled: z.boolean(),
  cron_expression: z
    .string()
    .trim()
    .min(1, "Cron expression is required")
    .refine((value) => describeCron(value).isValid, {
      message: "Invalid cron expression",
    }),
  timezone: z
    .string()
    .min(1)
    .refine((value) => isValidTimezone(value), { message: "Invalid timezone" }),
  next_run: z.string().optional(),
});

const manualTriggerConfigSchema = z.object({
  type: z.literal("manual"),
  enabled: z.boolean(),
});

// Generic trigger config for all integration triggers (gmail, calendar, slack, etc.)
// Only validates base fields - backend validates trigger-specific fields
// This allows new triggers to be added without frontend schema changes
const integrationTriggerConfigSchema = z
  .object({
    type: z.string(),
    enabled: z.boolean(),
  })
  .catchall(z.unknown()); // Allow any additional properties

// Combined trigger config - tries built-in first, then falls back to generic
const triggerConfigSchema = z.union([
  scheduleTriggerConfigSchema,
  manualTriggerConfigSchema,
  integrationTriggerConfigSchema,
]);

// =============================================================================
// MAIN FORM SCHEMA
// =============================================================================

export const workflowFormSchema = z.object({
  title: z.string().min(1, "Title is required").max(100, "Title too long"),
  description: z
    .string()
    .max(300, "Description too long (max 300 characters)")
    .transform((val) => val.trim() || undefined)
    .optional(),
  prompt: z.string().min(1, "Prompt is required").max(5000, "Prompt too long"),
  activeTab: z.enum(["manual", "schedule", "trigger"]),
  selectedTrigger: z.string(),
  trigger_config: triggerConfigSchema,
  notify_on_completion: z.boolean(),
});

// Export the inferred type
export type WorkflowFormData = z.infer<typeof workflowFormSchema>;

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Get default form values for new workflow creation.
 */
export const getDefaultFormValues = (): WorkflowFormData => ({
  title: "",
  description: undefined,
  prompt: "",
  activeTab: "manual",
  selectedTrigger: "",
  trigger_config: {
    type: "manual",
    enabled: true,
  },
  notify_on_completion: true,
});

/**
 * Convert existing workflow to form data for editing.
 * Works generically for any trigger type.
 */
export const workflowToFormData = (workflow: Workflow): WorkflowFormData => {
  const triggerType = workflow.trigger_config.type;

  // For integration triggers, try multiple locations for the specific trigger slug:
  // 1. trigger_name at top level (new format)
  // 2. trigger_data.trigger_name (properly structured format)
  // 3. type field itself (old format, where type contained the specific slug)
  const triggerName = (workflow.trigger_config as { trigger_name?: string })
    .trigger_name;
  const triggerDataName = (
    workflow.trigger_config as { trigger_data?: { trigger_name?: string } }
  ).trigger_data?.trigger_name;

  // Determine which tab should be active
  // Built-in triggers: schedule, manual -> their respective tabs
  // Everything else (gmail, calendar, slack, etc.) -> trigger tab
  const isBuiltInTrigger =
    triggerType === "schedule" || triggerType === "manual";
  const activeTab = isBuiltInTrigger
    ? (triggerType as "schedule" | "manual")
    : "trigger";

  // For integration triggers, selectedTrigger is the specific trigger slug.
  // Priority: trigger_name > trigger_data.trigger_name > type (for old format)
  // Note: For category-based types (calendar, email, app), we need the slug, not the category
  const isCategory = ["calendar", "email", "app"].includes(triggerType);
  const selectedTrigger = isBuiltInTrigger
    ? ""
    : triggerName || triggerDataName || (isCategory ? "" : triggerType);

  return {
    title: workflow.title,
    description: workflow.description || undefined,
    prompt: workflow.prompt || workflow.description || workflow.title,
    activeTab,
    selectedTrigger,
    trigger_config: workflow.trigger_config,
    notify_on_completion: workflow.notify_on_completion ?? true,
  };
};
